import { format, isValid } from 'date-fns'

/** Excel epoch基準日（時刻のみセルの日付部分） */
const EXCEL_EPOCHS = [
  [1899, 11, 30],
  [1900, 0, 1],
  [1904, 0, 1]
]

/** 組込みnumFmt ID → 書式文字列（主要な日付・時刻） */
const BUILTIN_NUM_FMT = {
  // 14 は「短い日付」= ロケール依存書式。ExcelJS がファイルから読む値も mm-dd-yy（2桁年）。
  // 2桁年として扱い、表示側で yyyy/MM/dd に正規化する。
  14: 'mm-dd-yy',
  15: 'd-mmm-yy',
  16: 'd-mmm',
  17: 'mmm-yy',
  18: 'h:mm AM/PM',
  19: 'h:mm:ss AM/PM',
  20: 'h:mm',
  21: 'h:mm:ss',
  22: 'm/d/yy h:mm',
  45: 'mm:ss',
  46: '[h]:mm:ss',
  47: 'mm:ss.0'
}

/**
 * @param {string | number | null | undefined} numFmt
 * @returns {string | null}
 */
function normalizeNumFmt(numFmt) {
  if (numFmt == null || numFmt === '') return null
  if (typeof numFmt === 'number') return BUILTIN_NUM_FMT[numFmt] ?? null
  return String(numFmt)
}

/**
 * @param {Date} date
 * @returns {boolean}
 */
function isExcelEpochDate(date) {
  const candidates = [
    [date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()],
    [date.getFullYear(), date.getMonth(), date.getDate()]
  ]
  return EXCEL_EPOCHS.some(([ey, em, ed]) => candidates.some(([y, m, d]) => y === ey && m === em && d === ed))
}

/**
 * ExcelJS の時刻セルは UTC 成分が Excel 表示値に対応する。 date-fns format（ローカル TZ）で +9h ずれるのを防ぐため、UTC 壁時計をローカル Date に載せ替える。
 *
 * @param {Date} date
 * @returns {Date}
 */
function toUtcWallClockDate(date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds()
  )
}

/**
 * @param {Date} date
 * @param {'date' | 'time' | 'datetime'} category
 * @returns {Date}
 */
function resolveFormatDate(date, category) {
  if (category === 'date') return date
  return toUtcWallClockDate(date)
}

/**
 * Excel numFmt からロケール指定・色指定等の [...] ブロックを除去する 例: [$-F800]h:mm → h:mm（date-fns 未対応の F 等を含むため必須）
 *
 * @param {string} numFmt
 * @returns {string}
 */
function stripExcelFormatDecorations(numFmt) {
  return numFmt
    .replace(/"[^"]*"/g, '')
    .replace(/\[\$[^\]]*\]/g, '')
    .replace(/\[(?!h{1,2}\]|m{1,2}\]|s{1,2}\])[^\]]*\]/gi, '')
    .replace(/;@$/, '')
    .replace(/@$/, '')
    .trim()
}

/**
 * @param {string} numFmt
 * @returns {string}
 */
function stripNumFmtLiterals(numFmt) {
  return stripExcelFormatDecorations(numFmt)
}

/**
 * @param {string | number | null | undefined} numFmt
 * @returns {'date' | 'time' | 'datetime' | null}
 */
function classifyNumFmt(numFmt) {
  const normalized = normalizeNumFmt(numFmt)
  if (!normalized) return null

  const fmt = stripNumFmtLiterals(normalized.split(';')[0].replace(/@$/, '')).toLowerCase()
  const stripped = fmt.replace(/\[.*?\]/g, '').trim()

  if (/^m{1,2}:s{1,2}/.test(stripped)) {
    return 'time'
  }

  const hasYear = /y+/.test(fmt)
  const hasDay = /d+/.test(fmt)
  const hasHour = /\[h+\]|(?:^|[^y])h{1,2}(?::|[^h])/.test(fmt.replace(/y+/g, ''))
  const hasSecond = /s+/.test(stripped)
  const hasDate = hasYear || hasDay

  if (hasDate && hasHour) return 'datetime'
  if (hasDate) return 'date'
  if (hasHour || hasSecond) return 'time'

  return null
}

/**
 * @param {string} fmt
 * @returns {string}
 */
function convertExcelDatePart(fmt) {
  return fmt
    .replace(/yyyy/gi, 'yyyy')
    .replace(/yy/gi, 'yy')
    .replace(/dd/gi, 'dd')
    .replace(/d/gi, 'd')
    .replace(/mm/gi, 'MM')
    .replace(/m/gi, 'M')
}

/**
 * @param {string} fmt
 * @returns {string}
 */
function convertExcelTimePart24h(fmt) {
  let result = fmt.replace(/\[h+\]/gi, (match) => match.replace(/\[|\]/g, ''))
  result = result.replace(/hh/gi, 'HH')
  result = result.replace(/ss/gi, 'ss')
  result = result.replace(/mm/gi, 'mm')
  result = result.replace(/(^|[^hH])h([^hHmMsS]|$)/gi, '$1H$2')
  return result
}

/**
 * @param {string} fmt
 * @returns {string}
 */
function convertExcelTimePart(fmt) {
  const stripped = fmt.replace(/\[.*?\]/g, '').trim()
  if (/^m{1,2}:s{1,2}/i.test(stripped)) {
    return stripped.replace(/mm/i, 'mm').replace(/^m/i, 'm').replace(/ss/i, 'ss')
  }

  const withoutAmPm = fmt.replace(/\s*AM\/PM/gi, '').replace(/\s*A\/P/gi, '')
  return convertExcelTimePart24h(withoutAmPm)
}

/**
 * @param {string | number | null | undefined} numFmt
 * @param {'date' | 'time' | 'datetime'} category
 * @returns {string | null}
 */
function excelNumFmtToDateFns(numFmt, category) {
  const normalized = normalizeNumFmt(numFmt)
  if (!normalized) return null

  const fmt = stripExcelFormatDecorations(normalized.split(';')[0])
  if (!fmt) return null

  if (category === 'date') {
    return convertExcelDatePart(fmt)
  }

  if (category === 'time') {
    return convertExcelTimePart(fmt)
  }

  const spaceIdx = fmt.search(/\s/)
  if (spaceIdx > 0) {
    const datePart = fmt.slice(0, spaceIdx)
    const timePart = fmt.slice(spaceIdx + 1)
    return `${convertExcelDatePart(datePart)} ${convertExcelTimePart(timePart)}`
  }

  return null
}

/**
 * @param {'date' | 'time' | 'datetime'} category
 * @param {Date} date
 * @returns {string}
 */
function getDefaultFormat(category, date) {
  if (category === 'time') {
    if (date.getUTCSeconds() !== 0 || date.getUTCMilliseconds() !== 0) {
      return 'HH:mm:ss'
    }
    return 'HH:mm'
  }
  if (category === 'datetime') {
    return 'yyyy/MM/dd HH:mm:ss'
  }
  return 'yyyy/MM/dd'
}

/** Excel の時刻シリアル基準日（UTC）。経過時間算出に用いる。 */
const EXCEL_TIME_EPOCH_UTC = Date.UTC(1899, 11, 30, 0, 0, 0)

/**
 * NumFmt が「経過時間（累計時間）」書式かどうかを判定する。
 *
 * Excel では角括弧付きの時要素 [h] / [hh] が「24 時間で繰り上げない累計時間」を表す。 例: [h]:mm, [hh]:mm:ss。これがある場合のみ total 時間として表示する。 角括弧が無い h:mm
 * 等は通常の時刻（0-23）または日時であり、累計変換しない （24h を超える値は Excel が日付へ繰り上げて保持しているため、その日時で表示する）。
 *
 * @param {string | number | null | undefined} numFmt
 * @returns {boolean}
 */
function isElapsedTimeFormat(numFmt) {
  const normalized = normalizeNumFmt(numFmt)
  if (!normalized) return false
  // ロケール等の [$...] 装飾を除いたうえで [h] / [hh] を検出する。
  const withoutLocale = normalized.replace(/\[\$[^\]]*\]/g, '')
  return /\[h+\]/i.test(withoutLocale)
}

/**
 * 累計時間（[h] 書式）を、24h で繰り上げずに total 時間として整形する。 date-fns の HH は 0-23 に丸まるため使えない（124:50:00 が 04:50:00 になる）。
 *
 * @param {Date} date Epoch(1899/12/30) 起点の経過時間を表す Date
 * @param {boolean} withSeconds 秒まで出力するか
 * @returns {string}
 */
function formatElapsedTime(date, withSeconds) {
  const totalSeconds = Math.round((date.getTime() - EXCEL_TIME_EPOCH_UTC) / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const mm = String(minutes).padStart(2, '0')
  if (withSeconds) {
    return `${hours}:${mm}:${String(seconds).padStart(2, '0')}`
  }
  return `${hours}:${mm}`
}

/**
 * NumFmt に秒成分が含まれるか（[h]:mm:ss など）。
 *
 * @param {string | number | null | undefined} numFmt
 * @returns {boolean}
 */
function numFmtHasSeconds(numFmt) {
  const normalized = normalizeNumFmt(numFmt)
  if (!normalized) return false
  return /s/i.test(normalized.replace(/\[\$[^\]]*\]/g, ''))
}

/**
 * ExcelJS Dateセルの表示用文字列を numFmt に従って整形する
 *
 * @param {Date | string | number | null | undefined} value
 * @param {string | number | null | undefined} numFmt
 * @returns {string}
 */
export function formatExcelDateCellDisplay(value, numFmt) {
  if (value == null || value === '') return ''

  const date = value instanceof Date ? value : new Date(value)
  if (!isValid(date)) return String(value)

  // [h]/[hh] 書式の累計時間は 24h で繰り上げず total 時間で表示する。
  // （日時に繰り上がった値も epoch 起点の経過時間として復元できる）
  if (isElapsedTimeFormat(numFmt)) {
    return formatElapsedTime(date, numFmtHasSeconds(numFmt))
  }

  let category = classifyNumFmt(numFmt)
  if (!category) {
    // numFmt 不明時: epoch ちょうどの時刻なら time、それ以外は date。
    category = isExcelEpochDate(date) ? 'time' : 'date'
  }

  let dateFnsFmt = excelNumFmtToDateFns(numFmt, category) ?? getDefaultFormat(category, date)
  // 日付セルは「数値の年月日（年4桁）」だけをそのまま表示し、それ以外は yyyy/MM/dd に正規化する。
  // ロケール依存書式（[$-...]）・曜日/月名（dddd/ddd/mmmm/mmm/aaa/aaaa）・和暦（ggg/e）・
  // 引用リテラル（"年" 等）・2桁年は、OS/表示ロケールを再現できず Excel と表示が食い違うため。
  // （日付の中身の値は正しいので、利用者は必要に応じてマッピングの日付変換で書式を変更する想定。）
  if (category === 'date' && !isSafeNumericDateFormat(numFmt)) {
    dateFnsFmt = 'yyyy/MM/dd'
  }
  const formatDate = resolveFormatDate(date, category)
  try {
    return format(formatDate, dateFnsFmt)
  } catch {
    return format(formatDate, getDefaultFormat(category, date))
  }
}

/**
 * ExcelJS の生セル値をプレビュー描画用のプリミティブにする。 日付・時刻・日時（Date）とリッチテキストは React が object を子要素にできず白画面になるため文字列化する。
 *
 * @param {unknown} raw Cell.value または cell.result
 * @param {string | number | null | undefined} numFmt
 * @returns {unknown} String / number / boolean / 空。残りの object は表示可能な文字列へ寄せる
 */
export function toExcelPreviewDisplayValue(raw, numFmt) {
  if (raw == null || raw === '') return raw
  if (raw instanceof Date) return formatExcelDateCellDisplay(raw, numFmt)
  if (typeof raw !== 'object') return raw

  if (Array.isArray(raw.richText)) {
    return raw.richText.map((part) => (part?.text != null ? String(part.text) : '')).join('')
  }
  if (typeof raw.text === 'string') return raw.text
  if (typeof raw.error === 'string') return raw.error
  return ''
}

/**
 * 「そのまま表示してよい数値の日付書式」かどうかを判定する。
 *
 * 安全とみなす条件（すべて満たす）:
 *
 * - ロケール装飾 [$-...] や色 [..] 等を含まない
 * - 引用リテラル "..." を含まない
 * - 年・月・日が数値トークン（y/m/d）と区切り（/ - . 空白）だけで構成される
 * - 年が4桁（yyyy）で指定されている（2桁年 yy は不可）
 * - 月名/曜日（mmm/mmmm/ddd/dddd/aaa/aaaa）や和暦（ggg/e）を含まない
 *
 * @param {string | number | null | undefined} numFmt
 * @returns {boolean}
 */
function isSafeNumericDateFormat(numFmt) {
  // builtin ID（数値）はロケール依存の短い日付等が多いため安全扱いしない。
  if (typeof numFmt !== 'string') return false
  const body = numFmt.split(';')[0]
  // ロケール/色等の角括弧、引用リテラルがあれば安全でない。
  if (/\[[^\]]*\]/.test(body)) return false
  if (/"/.test(body)) return false
  const lower = body.toLowerCase().trim()
  // 許可するトークンと区切りのみで構成されているか（m{1,2}/d{1,2}/yyyy と / - . 空白）。
  // 月名 mmm+ や曜日 ddd+・aaa・和暦 g/e は許可しない。
  if (!/^[ymd/\-.\s]+$/.test(lower)) return false
  // 年は4桁必須（yy 単独は不可）。yyyy を除いた残りに y が無いことを確認。
  if (!/y{4}/.test(lower)) return false
  if (/y/.test(lower.replace(/y{4}/g, ''))) return false
  // 月名（mmm 以上）・曜日（ddd 以上）は不可。
  if (/m{3,}/.test(lower)) return false
  if (/d{3,}/.test(lower)) return false
  return true
}
